import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle, Warning, WarningOctagon, Info } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { toast } from "sonner";

const SEV_ICON = {
  critical: WarningOctagon,
  warning: Warning,
  info: Info,
};
const SEV_COLOR = {
  critical: "text-red-600",
  warning: "text-amber-600",
  info: "text-blue-900",
};

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "hace unos segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return d.toLocaleString();
}

export default function NotificationsBell() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [list, count] = await Promise.all([
        api.get("/notifications?limit=30"),
        api.get("/notifications/unread-count"),
      ]);
      setItems(list.data || []);
      setUnread(count.data?.unread || 0);
    } catch (_e) {
      /* silent — bell should not throw */
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const openItem = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          aria-label={t("notifications")}
        >
          <Bell size={18} weight="bold" />
          {unread > 0 && (
            <span
              data-testid="notifications-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="notifications-popover"
        align="end"
        className="w-96 p-0 bg-white"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <p className="font-heading font-bold text-slate-900">{t("notifications")}</p>
          {unread > 0 && (
            <Button
              data-testid="notifications-mark-all-read"
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-blue-900"
              onClick={markAll}
            >
              <CheckCircle size={14} weight="bold" className="mr-1" /> {t("markAllRead")}
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[70vh]">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="notifications-empty">
              {t("noNotifications")}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((n) => {
                const Icon = SEV_ICON[n.severity] || Info;
                return (
                  <li key={n.id}>
                    <button
                      data-testid={`notification-item-${n.id}`}
                      onClick={() => openItem(n)}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex gap-3 ${
                        n.read ? "opacity-70" : "bg-blue-50/40"
                      }`}
                    >
                      <Icon size={20} weight="fill" className={`shrink-0 mt-0.5 ${SEV_COLOR[n.severity] || "text-blue-900"}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">
                          {!n.read && <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-900 mr-1.5 align-middle" />}
                          {n.title}
                        </p>
                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{fmtWhen(n.created_at)}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
